import React, { useEffect, useRef, useState } from 'react';
import type { Track } from '../types/Track';

export type VisualizerMode = 'spectrum' | 'wave' | 'orbit';

type Props = {
  track: Track | null;
  isPlaying: boolean;
  currentTime: number;
  onClose: () => void;
};

const palettes: Record<VisualizerMode, [string, string, string]> = {
  spectrum: ['#f6f7f8', '#aab3c0', '#5d6878'],
  wave: ['#dfe5ec', '#8794a7', '#3f4c5f'],
  orbit: ['#ffffff', '#b9c5d5', '#617189'],
};

export default function AudioVisualizer({ track, isPlaying, currentTime, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [mode, setMode] = useState<VisualizerMode>('spectrum');
  const [intensity, setIntensity] = useState(72);
  const [glow, setGlow] = useState(true);
  const [beatPulse, setBeatPulse] = useState(true);
  const [syncStrength, setSyncStrength] = useState(78);
  const [trail, setTrail] = useState(0);
  const [micEnabled, setMicEnabled] = useState(false);
  const [micError, setMicError] = useState('');
  const [micLevel, setMicLevel] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const stopMicrophone = () => {
      streamRef.current?.getTracks().forEach(trackItem => trackItem.stop());
      streamRef.current = null;
      analyserRef.current?.disconnect();
      analyserRef.current = null;
      void audioContextRef.current?.close();
      audioContextRef.current = null;
    };
    if (!micEnabled) { stopMicrophone(); return () => stopMicrophone(); }
    setMicError('');
    if (!navigator.mediaDevices?.getUserMedia) { setMicEnabled(false); setMicError('This browser does not expose microphone access.'); return () => stopMicrophone(); }
    void navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } }).then(async stream => {
      if (cancelled) { stream.getTracks().forEach(trackItem => trackItem.stop()); return; }
      const AudioContextConstructor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextConstructor) throw new Error('Audio analysis is not supported by this browser.');
      const audioContext = new AudioContextConstructor();
      audioContextRef.current = audioContext;
      await audioContext.resume();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      streamRef.current = stream;
      analyserRef.current = analyser;
    }).catch(() => { if (!cancelled) { setMicEnabled(false); setMicError('Microphone access was not granted.'); } });
    return () => { cancelled = true; stopMicrophone(); };
  }, [micEnabled]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    let frame = 0;
    let animation = 0;

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * ratio));
      canvas.height = Math.max(1, Math.floor(rect.height * ratio));
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const render = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const time = currentTime + frame / 60;
      const trackSeed = track?.videoId ? hashTrack(track.videoId) : 0.35;
      const bassPulse = 0.5 + 0.5 * Math.sin(time * 2 * Math.PI * 1.8 + trackSeed * 6.28);
      const accentPulse = Math.pow(Math.max(0, Math.sin(time * 2 * Math.PI * 3.6 + trackSeed * 11)), 5);
      const rhythmEnergy = isPlaying ? 0.24 + bassPulse * 0.45 + accentPulse * 0.28 : 0.16 + bassPulse * 0.08;
      const analyser = analyserRef.current;
      const frequencyData = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
      if (analyser && frequencyData) analyser.getByteFrequencyData(frequencyData);
      const microphoneEnergy = frequencyData ? Math.max(0.08, frequencyData.reduce((sum, value) => sum + value, 0) / frequencyData.length / 255) : 0;
      const syncEnergy = 0.25 + rhythmEnergy * (syncStrength / 100);
      const energy = frequencyData ? microphoneEnergy : syncEnergy;
      if (frequencyData && frame % 6 === 0) setMicLevel(microphoneEnergy);
      const amount = intensity / 100;
      const [bright, mid, deep] = palettes[mode];
      if (trail > 0) { context.fillStyle = `rgba(12, 14, 17, ${Math.max(0.04, 1 - trail / 100)})`; context.fillRect(0, 0, width, height); } else context.clearRect(0, 0, width, height);
      const background = context.createRadialGradient(width * 0.5, height * 0.5, 10, width * 0.5, height * 0.5, width * 0.75);
      background.addColorStop(0, '#303945');
      background.addColorStop(0.55, '#171b21');
      background.addColorStop(1, '#0c0e11');
      context.fillStyle = background;
      context.fillRect(0, 0, width, height);

      const renderAmount = beatPulse ? amount * (0.86 + bassPulse * 0.24) : amount;
      if (mode === 'spectrum') drawSpectrum(context, width, height, time, renderAmount, energy, glow, bright, mid, deep, frequencyData);
      if (mode === 'wave') drawWaves(context, width, height, time, renderAmount, energy, glow, bright, mid, deep);
      if (mode === 'orbit') drawOrbit(context, width, height, time, renderAmount, energy, glow, bright, mid, deep);

      frame += 1;
      animation = window.requestAnimationFrame(render);
    };
    render();
    return () => { window.removeEventListener('resize', resize); window.cancelAnimationFrame(animation); };
  }, [currentTime, glow, intensity, isPlaying, mode, syncStrength, beatPulse, trail, track?.videoId]);

  return <div className="visualizer-backdrop" role="dialog" aria-modal="true" aria-label="Audio visualizer">
    <section className="visualizer-panel">
      <div className="visualizer-topbar">
        <div><span className="overline">NOW VISUALIZING</span><h2>{track?.title || 'Choose a track to begin'}</h2><p>{track?.channelTitle || 'Playback-synced sound design'}</p></div>
        <button className="visualizer-close" onClick={onClose} aria-label="Close visualizer">×</button>
      </div>
      <div className="visualizer-stage"><canvas ref={canvasRef} /><div className="visualizer-stage-label"><span>{micEnabled ? 'MIC LIVE' : isPlaying ? 'RHYTHM SYNC' : 'PAUSED'}</span><span>{mode.toUpperCase()}</span></div></div>
      <div className="visualizer-controls">
        <div className="visualizer-modes"><span className="overline">STYLE</span><div>{(['spectrum', 'wave', 'orbit'] as VisualizerMode[]).map(item => <button key={item} className={mode === item ? 'visualizer-mode active' : 'visualizer-mode'} onClick={() => setMode(item)}>{item === 'spectrum' ? 'Spectrum bars' : item === 'wave' ? 'Flowing wave' : 'Orbital field'}</button>)}</div></div>
        <div className="visualizer-effect-group"><span className="overline">SYNC EFFECTS</span><label className="visualizer-range">Intensity <input type="range" min="20" max="100" value={intensity} onChange={event => setIntensity(Number(event.target.value))} /></label><label className="visualizer-range">Sync strength <input type="range" min="0" max="100" value={syncStrength} onChange={event => setSyncStrength(Number(event.target.value))} /></label><label className="visualizer-range">Motion trail <input type="range" min="0" max="75" value={trail} onChange={event => setTrail(Number(event.target.value))} /></label></div>
        <button className={beatPulse ? 'visualizer-toggle active' : 'visualizer-toggle'} onClick={() => setBeatPulse(value => !value)}>{beatPulse ? 'Beat pulse on' : 'Beat pulse off'}</button>
        <button className={glow ? 'visualizer-toggle active' : 'visualizer-toggle'} onClick={() => setGlow(value => !value)}>{glow ? 'Glow on' : 'Glow off'}</button>
        <button className={micEnabled ? 'visualizer-toggle mic-toggle active' : 'visualizer-toggle mic-toggle'} onClick={() => setMicEnabled(value => !value)}>{micEnabled ? 'Stop microphone' : 'Use microphone'}</button>
        {micEnabled && <span className="visualizer-meter" aria-label="Microphone input level"><i style={{ width: `${Math.round(micLevel * 100)}%` }} /></span>}
        {micError && <span className="visualizer-mic-error">{micError}</span>}
      </div>
    </section>
  </div>;
}

function hashTrack(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  return Math.abs(hash % 1000) / 1000;
}

function drawSpectrum(context: CanvasRenderingContext2D, width: number, height: number, time: number, amount: number, energy: number, glow: boolean, bright: string, mid: string, deep: string, levels: Uint8Array | null) {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) * 0.19;
  const bars = 72;
  context.save();
  context.translate(cx, cy);
  if (glow) { context.shadowBlur = 20; context.shadowColor = mid; }
  for (let i = 0; i < bars; i += 1) {
    const angle = (i / bars) * Math.PI * 2;
    const micPulse = levels ? (levels[Math.floor(i / bars * levels.length)] || 0) / 255 : 0;
    const pulse = levels ? Math.max(0.12, micPulse) : 0.35 + Math.abs(Math.sin(time * 2.1 + i * 0.41)) * 0.65;
    const length = (22 + pulse * 92 * amount * energy) * (0.8 + Math.sin(time * 0.7 + i) * 0.08);
    const inner = radius;
    const outer = radius + length;
    context.strokeStyle = i % 3 === 0 ? bright : i % 2 === 0 ? mid : deep;
    context.lineWidth = 2 + (i % 4 === 0 ? 1 : 0);
    context.beginPath();
    context.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
    context.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
    context.stroke();
  }
  context.shadowBlur = 0;
  context.fillStyle = '#e9edf2';
  context.beginPath();
  context.arc(0, 0, radius - 4, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = '#20252d';
  context.beginPath();
  context.arc(0, 0, radius - 15, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

function drawWaves(context: CanvasRenderingContext2D, width: number, height: number, time: number, amount: number, energy: number, glow: boolean, bright: string, mid: string, deep: string) {
  const colors = [bright, mid, deep];
  for (let layer = 0; layer < 3; layer += 1) {
    context.save();
    context.strokeStyle = colors[layer];
    context.lineWidth = layer === 0 ? 2.5 : 1.4;
    if (glow) { context.shadowBlur = 18 - layer * 4; context.shadowColor = colors[layer]; }
    context.beginPath();
    for (let x = 0; x <= width; x += 8) {
      const normalized = x / width;
      const envelope = Math.sin(normalized * Math.PI);
      const y = height / 2 + Math.sin(normalized * 14 + time * (1.1 + layer * 0.18)) * (26 + layer * 12) * envelope * amount * energy + Math.sin(normalized * 31 - time * 0.7) * 8 * envelope;
      if (x === 0) context.moveTo(x, y); else context.lineTo(x, y);
    }
    context.stroke();
    context.restore();
  }
}

function drawOrbit(context: CanvasRenderingContext2D, width: number, height: number, time: number, amount: number, energy: number, glow: boolean, bright: string, mid: string, deep: string) {
  const cx = width / 2;
  const cy = height / 2;
  const colors = [bright, mid, deep];
  context.save();
  context.translate(cx, cy);
  for (let ring = 0; ring < 4; ring += 1) {
    const radius = 54 + ring * 46;
    context.strokeStyle = `${colors[ring % 3]}55`;
    context.lineWidth = 1;
    context.beginPath();
    context.ellipse(0, 0, radius, radius * (0.42 + ring * 0.08), time * 0.17 + ring, 0, Math.PI * 2);
    context.stroke();
  }
  if (glow) { context.shadowBlur = 16; context.shadowColor = mid; }
  for (let i = 0; i < 34; i += 1) {
    const orbit = i % 4;
    const radius = 54 + orbit * 46;
    const angle = time * (0.3 + orbit * 0.08) + i * 0.74;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius * (0.42 + orbit * 0.08);
    const size = 1.5 + Math.abs(Math.sin(time * 2 + i)) * 3.5 * amount * energy;
    context.fillStyle = colors[i % 3];
    context.beginPath();
    context.arc(x, y, size, 0, Math.PI * 2);
    context.fill();
  }
  context.restore();
}
